import { get, groupBy, isEqual, keyBy, omit } from 'lodash';
import type { PlanningSchedulerStoreData } from './planningSchedulerCache';

export type TimelineEventChanges = { events: any[]; assignments: any[]; dependencies?: any[] };
export type ApplyTimelineEventChanges = (updates: TimelineEventChanges, eventIds: string[]) => Promise<void>;

/** Relationships are updated through the dependency and assignment stores. */
export const transformTimelineEventForUpdate = <T extends object>(event: T) => {
    const eventUpdate = omit(event, ['dependencies', 'resources']);
    return eventUpdate;
};

const getAssignmentEventId = (assignment: any) => {
    const eventReference = assignment?.eventId ?? assignment?.event;
    const eventId = get(eventReference, 'id', eventReference);
    return eventId;
};

export const assignmentIsForEvents = (assignment: any, ids: Set<string>) => {
    const eventId = getAssignmentEventId(assignment);
    const isAssignedToEvent = ids.has(eventId);
    return isAssignedToEvent;
};

export const dependencyIsForEvents = (dependency: any, ids: Set<string>) => {
    const fromReference = dependency?.fromEvent ?? dependency?.from;
    const toReference = dependency?.toEvent ?? dependency?.to;
    const fromId = get(fromReference, 'id', fromReference);
    const toId = get(toReference, 'id', toReference);
    const isRelatedToEvent = ids.has(fromId) || ids.has(toId);
    return isRelatedToEvent;
};

/** Replace returned entities; absence from an incremental result does not imply deletion. */
export const replaceTimelineEvents = (store: any, updates: any, eventIds: string[]) => {
    const ids = new Set(eventIds);
    const updatedTimelineData = {
        ...store,
        events: [...(store.events || []).filter((event: any) => !ids.has(event?.id)), ...(updates.events || [])],
        assignments: [
            ...(store.assignments || []).filter((assignment: any) => !assignmentIsForEvents(assignment, ids)),
            ...(updates.assignments || [])
        ],
        ...(updates.dependencies !== undefined
            ? {
                  dependencies: [
                      ...(store.dependencies || []).filter(
                          (dependency: any) => !dependencyIsForEvents(dependency, ids)
                      ),
                      ...updates.dependencies
                  ]
              }
            : {})
    };
    return updatedTimelineData;
};

/** Apply only fetched activity changes to the latest Planning store, preserving other writers' data. */
export const applyPlanningTimelineUpdates = (
    currentData: PlanningSchedulerStoreData | undefined,
    updates: PlanningSchedulerStoreData,
    eventIds: string[]
): PlanningSchedulerStoreData => {
    const replacedTimelineData = replaceTimelineEvents(currentData ?? {}, updates, eventIds);
    const updatedTimelineData: PlanningSchedulerStoreData = {
        ...replacedTimelineData,
        lastFetchedAt: updates.lastFetchedAt ?? currentData?.lastFetchedAt
    };
    return updatedTimelineData;
};

/** Retain cache edits and recorded SSE mutations made during an RM fetch, including absent-record deletions. */
export const reconcileReservationTimelineUpdates = (
    requestedData: any,
    currentData: any,
    updates: any,
    eventIds: string[],
    requestedMutationRevisions: ReadonlyMap<string, number> = new Map(),
    currentMutationRevisions: ReadonlyMap<string, number> = requestedMutationRevisions
) => {
    const latestData = currentData ?? requestedData;
    const requestedEvents = keyBy(requestedData?.events || [], 'id');
    const currentEvents = keyBy(latestData?.events || [], 'id');
    const requestedAssignments = groupBy(requestedData?.assignments || [], getAssignmentEventId);
    const currentAssignments = groupBy(latestData?.assignments || [], getAssignmentEventId);
    const replaceEventIds = eventIds.filter(
        (id) =>
            (requestedMutationRevisions.get(id) ?? 0) === (currentMutationRevisions.get(id) ?? 0) &&
            isEqual(requestedEvents[id], currentEvents[id]) &&
            isEqual(requestedAssignments[id] || [], currentAssignments[id] || [])
    );
    const replacedIds = new Set(replaceEventIds);
    const acceptedUpdates = {
        events: (updates.events || []).filter((event) => replacedIds.has(event.id)),
        assignments: (updates.assignments || []).filter((assignment) => assignmentIsForEvents(assignment, replacedIds)),
        ...(updates.lastFetchedAt !== undefined ? { lastFetchedAt: updates.lastFetchedAt } : {})
    };
    const formattedData = replaceTimelineEvents(latestData ?? {}, acceptedUpdates, replaceEventIds);
    formattedData.lastFetchedAt = updates.lastFetchedAt ?? latestData?.lastFetchedAt;
    const reconciledTimelineData = { formattedData, updates: acceptedUpdates, replaceEventIds };
    return reconciledTimelineData;
};

/** Reconcile Planning refreshes without restoring activities changed while the request was in flight. */
export const reconcilePlanningTimelineUpdates = (
    requestedData: PlanningSchedulerStoreData,
    currentData: PlanningSchedulerStoreData | undefined,
    updates: PlanningSchedulerStoreData,
    eventIds: string[],
    requestedMutationRevisions: ReadonlyMap<string, number>,
    currentMutationRevisions: ReadonlyMap<string, number>
) => {
    const latestData = currentData ?? requestedData;
    const reconciledData = reconcileReservationTimelineUpdates(
        requestedData,
        latestData,
        updates,
        eventIds,
        requestedMutationRevisions,
        currentMutationRevisions
    );
    const acceptedIds = new Set<string>(reconciledData.replaceEventIds);
    const protectedIds = new Set(eventIds.filter((id) => !acceptedIds.has(id)));
    for (const [id, revision] of currentMutationRevisions) {
        if (revision !== (requestedMutationRevisions.get(id) ?? 0)) protectedIds.add(id);
    }
    const acceptedUpdates: PlanningSchedulerStoreData = {
        ...reconciledData.updates,
        ...(updates.dependencies !== undefined
            ? {
                  dependencies: [
                      ...(latestData.dependencies || []).filter(
                          (dependency) =>
                              dependencyIsForEvents(dependency, acceptedIds) &&
                              dependencyIsForEvents(dependency, protectedIds)
                      ),
                      ...updates.dependencies.filter(
                          (dependency) =>
                              dependencyIsForEvents(dependency, acceptedIds) &&
                              !dependencyIsForEvents(dependency, protectedIds)
                      )
                  ]
              }
            : {})
    };
    return {
        formattedData: applyPlanningTimelineUpdates(latestData, acceptedUpdates, reconciledData.replaceEventIds),
        updates: acceptedUpdates,
        replaceEventIds: reconciledData.replaceEventIds
    };
};
